import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col, Card, Spinner, Modal, Table, Badge, Tooltip, OverlayTrigger } from 'react-bootstrap'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import { inventoryItemAPI } from '../api/inventoryItemAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import QuantityPicker from '../components/QuantityPicker'
import QuantityDisplay from '../components/QuantityDisplay'
import CopyableText from '../components/CopyableText'
import StockItemInfo from '../components/StockItemInfo'
import { usePermissions } from '../contexts/PermissionContext'
import Select from 'react-select'
import '../styles/selectControl.css'

function InventoryDisassembleScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()
    const { inventoryItemId } = useParams()
    const [returnFilters] = useState(location.state?.returnFilters || '')

    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(null)
    const [showConfirmModal, setShowConfirmModal] = useState(false)

    const [inventoryItem, setInventoryItem] = useState(null)
    const [productionLot, setProductionLot] = useState(null)
    const [allocations, setAllocations] = useState([])

    const [outputQuantity, setOutputQuantity] = useState('')
    const [warehouses, setWarehouses] = useState([])
    const [locations, setLocations] = useState([])
    const [selectedWarehouse, setSelectedWarehouse] = useState(null)
    const [selectedLocationId, setSelectedLocationId] = useState('')

    const [selectedAllocations, setSelectedAllocations] = useState({})
    const [restoreQuantities, setRestoreQuantities] = useState({})

    const outputQtyRef = useRef(null)

    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true)
                const [info, warehouseList] = await Promise.all([
                    inventoryItemAPI.getDisassemblyInfo(inventoryItemId),
                    warehouseAPI.getAll()
                ])

                setInventoryItem(info.inventory_item)
                setProductionLot(info.production_lot)
                setAllocations(info.allocations)
                setWarehouses(warehouseList)

                const itemWarehouseId = info.inventory_item?.stock_item ? null : null
                const itemLocation = info.inventory_item?.location_name
                const itemWarehouse = info.inventory_item?.warehouse_name
                const matchingWarehouse = warehouseList.find(w => w.name === itemWarehouse)
                if (matchingWarehouse) {
                    setSelectedWarehouse(matchingWarehouse)
                }

                const defaultSelected = {}
                const defaultQuantities = {}
                info.allocations.forEach(alloc => {
                    if (!alloc.stock_item_deleted) {
                        defaultSelected[alloc._id] = true
                        defaultQuantities[alloc._id] = alloc.max_restorable
                    }
                })
                setSelectedAllocations(defaultSelected)
                setRestoreQuantities(defaultQuantities)
            } catch (err) {
                setError(formatErrorResponse(err))
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [inventoryItemId])

    useEffect(() => {
        let isMounted = true
        async function fetchLocations() {
            if (!selectedWarehouse) {
                setLocations([])
                return
            }
            try {
                const warehouseLocations = await locationAPI.getAll(selectedWarehouse._id, undefined, undefined, undefined, undefined, true)
                if (!isMounted) return
                setLocations(warehouseLocations)

                if (inventoryItem) {
                    const currentLoc = warehouseLocations.find(l => l.name === inventoryItem.location_name)
                    if (currentLoc && !selectedLocationId) {
                        setSelectedLocationId(currentLoc._id)
                    }
                }
            } catch (err) {
                if (isMounted) setError('Failed to load locations')
            }
        }
        fetchLocations()
        return () => { isMounted = false }
    }, [selectedWarehouse?._id])

    const handleToggleAllocation = (allocId) => {
        setSelectedAllocations(prev => {
            const updated = { ...prev }
            if (updated[allocId]) {
                delete updated[allocId]
            } else {
                updated[allocId] = true
                if (!restoreQuantities[allocId]) {
                    const alloc = allocations.find(a => a._id === allocId)
                    if (alloc) {
                        setRestoreQuantities(prev => ({ ...prev, [allocId]: alloc.max_restorable }))
                    }
                }
            }
            return updated
        })
    }

    const handleRestoreQuantityChange = (allocId, qty) => {
        setRestoreQuantities(prev => ({ ...prev, [allocId]: qty }))
    }

    const validate = () => {
        if (!outputQuantity || Number(outputQuantity) <= 0) {
            setError('Please enter the quantity to consume')
            outputQtyRef.current?.focus()
            return false
        }
        if (!selectedLocationId) {
            setError('Please select a destination location')
            return false
        }
        const selectedCount = Object.keys(selectedAllocations).length
        if (selectedCount === 0) {
            setError('Please select at least one ingredient to restore')
            return false
        }
        for (const allocId of Object.keys(selectedAllocations)) {
            const qty = restoreQuantities[allocId]
            if (!qty || Number(qty) <= 0) {
                const alloc = allocations.find(a => a._id === Number(allocId))
                setError(`Please enter a restore quantity for ${alloc?.stock_item_name || 'ingredient'}`)
                return false
            }
        }
        return true
    }

    const handleSubmit = () => {
        setError(null)
        if (!validate()) return
        setShowConfirmModal(true)
    }

    const handleConfirm = async () => {
        setShowConfirmModal(false)
        setSubmitting(true)
        setError(null)

        const restorations = Object.keys(selectedAllocations).map(allocId => ({
            production_lot_inventory_item_id: Number(allocId),
            quantity_to_restore: Number(restoreQuantities[allocId]),
        }))

        try {
            await inventoryItemAPI.disassemble(inventoryItemId, {
                output_quantity_to_consume: Number(outputQuantity),
                destination_location_id: Number(selectedLocationId),
                restorations,
            })
            navigate(`/inventory${returnFilters}${returnFilters.includes('?') ? '&' : '?'}refresh=1`)
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setSubmitting(false)
        }
    }

    const warehouseOptions = warehouses.map(w => ({ value: w._id, label: w.name }))
    const locationOptions = locations.map(l => ({ value: l._id, label: l.name, item_count: l.item_count }))

    if (permissionsLoading || loading) {
        return (
            <Container className="py-3">
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    if (!hasPermission('production.manage')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to disassemble production items.
                </Alert>
            </Container>
        )
    }

    if (!inventoryItem || !productionLot) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    {error || 'Failed to load disassembly information'}
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate(`/inventory${returnFilters}`)}>
                    <i className="fas fa-arrow-left me-2"></i>Back to Inventory
                </Button>
            </Container>
        )
    }

    const stockItem = inventoryItem.stock_item
    const maxDisassemblable = Number(productionLot.max_disassemblable)

    return (
        <Container className="py-3">
            <h2>Disassemble: {stockItem?.description}</h2>
            <div className="mb-3">
                <div className="mb-1">
                    <Badge bg="secondary" className="me-2">Production LOT</Badge>
                    <a href={`/production/${productionLot._id}`} target="_blank" rel="noopener noreferrer">
                        {productionLot.lot_number}
                    </a>
                </div>
                <div>
                    <Badge bg="secondary" className="me-2">Recipe</Badge>
                    <a href={`/recipe/${productionLot.recipe_id}`} target="_blank" rel="noopener noreferrer">
                        {productionLot.recipe_name}
                    </a>
                </div>
            </div>

            <Row className="justify-content-left">
                <Col style={{ maxWidth: '730px' }}>
                    <Card className="mb-3">
                        <Card.Header><strong>Inventory Item</strong></Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                {stockItem?.image && (
                                    <Col xs="auto">
                                        <img
                                            src={stockItem.image}
                                            alt={stockItem.description}
                                            style={{ width: '120px', height: '120px', objectFit: 'contain' }}
                                        />
                                    </Col>
                                )}
                                <Col>
                                    <h5 className="mb-1">{stockItem?.description}</h5>
                                    {stockItem?.gtin && <CopyableText value={stockItem.gtin} toastLabel="GTIN" />}
                                </Col>
                            </Row>
                            <Row className="mb-2">
                                <Col xs={4} className="text-muted">Current Quantity</Col>
                                <Col>
                                    <QuantityDisplay
                                        casePack={stockItem?.case_pack || 1}
                                        uom={stockItem?.uom || ''}
                                        value={inventoryItem.quantity}
                                    />
                                </Col>
                            </Row>
                            <Row className="mb-2">
                                <Col xs={4} className="text-muted">Location</Col>
                                <Col>{inventoryItem.warehouse_name} / {inventoryItem.location_name}</Col>
                            </Row>
                            <Row className="mb-2">
                                <Col xs={4} className="text-muted">Produced</Col>
                                <Col>
                                    <QuantityDisplay
                                        casePack={Number(productionLot.output_stock_item_case_pack) || 1}
                                        uom={productionLot.output_stock_item_uom || ''}
                                        value={Number(productionLot.actual_quantity_produced)}
                                    />
                                </Col>
                            </Row>
                            {Number(productionLot.quantity_disassembled) > 0 && (
                                <Row className="mb-2">
                                    <Col xs={4} className="text-muted">Already Disassembled</Col>
                                    <Col>
                                        <QuantityDisplay
                                            casePack={Number(productionLot.output_stock_item_case_pack) || 1}
                                            uom={productionLot.output_stock_item_uom || ''}
                                            value={Number(productionLot.quantity_disassembled)}
                                        />
                                    </Col>
                                </Row>
                            )}
                            <hr />
                            <Form.Label>Quantity to Disassemble*</Form.Label>
                            <div style={{ maxWidth: '350px' }}>
                                <QuantityPicker
                                    ref={outputQtyRef}
                                    casePack={stockItem?.case_pack || 1}
                                    uom={stockItem?.uom || ''}
                                    value={outputQuantity}
                                    onChange={(qty) => setOutputQuantity(qty)}
                                    required
                                    max={maxDisassemblable}
                                />
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="mb-3">
                        <Card.Header><strong>Destination for Restored Ingredients</strong></Card.Header>
                        <Card.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Warehouse*</Form.Label>
                                <Select
                                    value={warehouseOptions.find(opt => opt.value === selectedWarehouse?._id) || null}
                                    onChange={(opt) => {
                                        const wh = warehouses.find(w => w._id === opt?.value)
                                        setSelectedWarehouse(wh || null)
                                        setSelectedLocationId('')
                                    }}
                                    options={warehouseOptions}
                                    placeholder="Select Warehouse..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Location*</Form.Label>
                                <Select
                                    value={locationOptions.find(opt => opt.value === selectedLocationId) || null}
                                    onChange={(opt) => setSelectedLocationId(opt?.value || '')}
                                    options={locationOptions}
                                    isDisabled={!selectedWarehouse}
                                    placeholder="Select Location..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    formatOptionLabel={(option) => (
                                        <span>
                                            {option.label}
                                            {option.item_count != null && (
                                                <span className={`location-badge${option.item_count === 0 ? ' is-empty' : ''}`}>
                                                    {option.item_count === 0 ? 'Empty' : `${option.item_count} items`}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                />
                            </Form.Group>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="mb-3">
                <Card.Header><strong>Ingredients to Restore</strong></Card.Header>
                <Card.Body className="p-0">
                    <Table striped hover responsive className="mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: '40px' }}></th>
                                <th style={{ width: '60px' }}>Image</th>
                                <th>GTIN</th>
                                <th>Ingredient</th>
                                <th>LOT</th>
                                <th>Used</th>
                                <th>Proportional Qty</th>
                                <th>Already Restored</th>
                                <th>Restore Quantity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allocations.map(alloc => {
                                const isDeleted = alloc.stock_item_deleted
                                const isSelected = !!selectedAllocations[alloc._id]
                                return (
                                    <tr key={alloc._id} className={isDeleted ? 'text-muted' : ''}>
                                        <td className="align-middle text-center">
                                            {isDeleted ? (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>Stock item deleted — cannot restore</Tooltip>}
                                                >
                                                    <i className="fas fa-ban text-danger"></i>
                                                </OverlayTrigger>
                                            ) : (
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => handleToggleAllocation(alloc._id)}
                                                />
                                            )}
                                        </td>
                                        <td className="align-middle">
                                            {alloc.stock_item_image ? (
                                                <OverlayTrigger
                                                    placement="right"
                                                    overlay={
                                                        <Tooltip className="stock-item-tooltip">
                                                            <StockItemInfo stockItem={{
                                                                description: alloc.stock_item_name,
                                                                gtin: alloc.stock_item_gtin,
                                                                image: alloc.stock_item_image,
                                                                case_pack: alloc.stock_item_case_pack,
                                                                uom: alloc.stock_item_uom,
                                                            }} />
                                                        </Tooltip>
                                                    }
                                                >
                                                    <img
                                                        src={alloc.stock_item_image}
                                                        alt={alloc.stock_item_name}
                                                        style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                                                    />
                                                </OverlayTrigger>
                                            ) : null}
                                        </td>
                                        <td className="align-middle">
                                            {alloc.stock_item_gtin ? <CopyableText value={alloc.stock_item_gtin} toastLabel="GTIN" /> : ''}
                                        </td>
                                        <td className="align-middle">
                                            {alloc.stock_item_name}
                                            {isDeleted && <Badge bg="danger" className="ms-2">Deleted</Badge>}
                                        </td>
                                        <td className="align-middle">{alloc.original_lot}</td>
                                        <td className="align-middle">
                                            <QuantityDisplay
                                                casePack={alloc.stock_item_case_pack || 1}
                                                uom={alloc.stock_item_uom || ''}
                                                value={Number(alloc.quantity_used)}
                                            />
                                        </td>
                                        <td className="align-middle">
                                            {(() => {
                                                const outQty = Number(outputQuantity)
                                                const totalProduced = Number(productionLot.actual_quantity_produced)
                                                if (!outQty || !totalProduced) return ''
                                                const proportional = (outQty / totalProduced) * Number(alloc.quantity_used)
                                                const maxRestorable = Number(alloc.max_restorable)
                                                const displayQty = Math.min(proportional, maxRestorable)
                                                return (
                                                    <QuantityDisplay
                                                        casePack={alloc.stock_item_case_pack || 1}
                                                        uom={alloc.stock_item_uom || ''}
                                                        value={displayQty}
                                                    />
                                                )
                                            })()}
                                        </td>
                                        <td className="align-middle">
                                            {Number(alloc.quantity_disassembled) > 0 ? (
                                                <QuantityDisplay
                                                    casePack={alloc.stock_item_case_pack || 1}
                                                    uom={alloc.stock_item_uom || ''}
                                                    value={Number(alloc.quantity_disassembled)}
                                                />
                                            ) : ''}
                                        </td>
                                        <td className="align-middle">
                                            {isDeleted ? (
                                                <span className="text-muted fst-italic">Cannot restore</span>
                                            ) : isSelected ? (
                                                <QuantityPicker
                                                    casePack={alloc.stock_item_case_pack || 1}
                                                    uom={alloc.stock_item_uom || ''}
                                                    value={restoreQuantities[alloc._id] || ''}
                                                    onChange={(qty) => handleRestoreQuantityChange(alloc._id, qty)}
                                                    max={Number(alloc.max_restorable)}
                                                />
                                            ) : ''}
                                        </td>
                                    </tr>
                                )
                            })}
                            {allocations.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="text-center text-muted py-3">
                                        No ingredients available for restoration
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            <div className="d-flex gap-2 mb-4">
                <Button
                    variant="warning"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            Disassembling...
                        </>
                    ) : 'Disassemble'}
                </Button>
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(`/inventory${returnFilters}`)}
                >
                    <i className="fas fa-arrow-left me-2"></i>Back to Inventory
                </Button>
            </div>

            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton className="bg-warning">
                    <Modal.Title>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Confirm Disassembly
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        You are about to consume{' '}
                        <strong>
                            <QuantityDisplay
                                casePack={stockItem?.case_pack || 1}
                                uom={stockItem?.uom || ''}
                                value={Number(outputQuantity)}
                            />
                        </strong>
                        {' '}of <strong>{stockItem?.description}</strong> and restore the following ingredients:
                    </p>
                    <ul>
                        {Object.keys(selectedAllocations).map(allocId => {
                            const alloc = allocations.find(a => a._id === Number(allocId))
                            if (!alloc) return null
                            return (
                                <li key={allocId}>
                                    {alloc.stock_item_name}: <QuantityDisplay
                                        casePack={alloc.stock_item_case_pack || 1}
                                        uom={alloc.stock_item_uom || ''}
                                        value={Number(restoreQuantities[allocId])}
                                    />
                                </li>
                            )
                        })}
                    </ul>
                    <p className="text-muted mb-0">This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="warning" onClick={handleConfirm}>
                        Yes, Disassemble
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}

export default InventoryDisassembleScreen
